// CountriesMultiSelect.tsx
import React, { useEffect, useMemo, useState } from "react";
import Select, { MultiValue, StylesConfig } from "react-select";
import { useFormContext } from "react-hook-form";
import useFetchCountries from "@pages/Project/utils/useFetchCountries";
import { toastService } from "@services/ToastService";

type Country = { id: number; name: string };
type CountryOption = { value: number; label: string };

interface Props {
  isEditing?: boolean;
  max?: number; // default 5
}

const CountriesMultiSelect: React.FC<Props> = ({
  isEditing = true,
  max = 5,
}) => {
  const { setValue, watch, trigger } = useFormContext();
  const { countries, isCountriesDataLoading } = useFetchCountries();

  const selectedIds: number[] = watch("countryIds") || [];
  const [selected, setSelected] = useState<CountryOption[]>([]);

  const options: CountryOption[] = useMemo(() => {
    if (!countries?.length) return [];
    return (countries as Country[]).map((c) => ({
      value: c.id,
      label: c.name,
    }));
  }, [countries]);

  useEffect(() => {
    if (!options.length) return;
    const mapped = options.filter((opt) => selectedIds.includes(opt.value));
    setSelected(mapped);
  }, [options, selectedIds.join(",")]);

  const handleChange = (newValue: MultiValue<CountryOption>) => {
    const next = [...newValue];
    if (next.length > max) {
      toastService.error(`Select up to ${max} countries`);
      next.splice(max);
    }
    setSelected(next);
    setValue(
      "countryIds",
      next.map((o) => o.value),
      { shouldDirty: true, shouldValidate: true }
    );
    trigger("countryIds");
  };

  const styles: StylesConfig<CountryOption, true> = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: "2.5rem",
        borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
        boxShadow: "none",
        "&:hover": { borderColor: state.isFocused ? "#3b82f6" : "#9ca3af" },
        backgroundColor: "white",
        cursor: isEditing ? "text" : "not-allowed",
        fontSize: "0.875rem",
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: "#DBEAFE",
        borderRadius: "9999px",
        paddingRight: 4,
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: "#1D4ED8",
        paddingLeft: 8,
        paddingRight: 4,
      }),
      multiValueRemove: (base) => ({
        ...base,
        color: "#2563EB",
        marginLeft: -4,
        ":hover": { backgroundColor: "transparent", color: "#1D4ED8" },
      }),
      placeholder: (base) => ({
        ...base,
        color: "#9ca3af",
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? "#F3F4F6" : "white",
        color: "#111827",
        fontSize: "0.875rem",
        ":active": { backgroundColor: "#E5E7EB" },
      }),
      valueContainer: (base) => ({
        ...base,
        gap: 6,
        paddingTop: 2,
        paddingBottom: 2,
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    }),
    [isEditing]
  );

  // Hide "x" remove button when not editing
  const customComponents = !isEditing ? { MultiValueRemove: () => null } : {};

  return (
    <Select<CountryOption, true>
      isMulti
      options={options}
      value={selected}
      onChange={handleChange}
      isDisabled={!isEditing}
      isLoading={isCountriesDataLoading}
      placeholder={`Select up to ${max} countries`}
      closeMenuOnSelect={false}
      hideSelectedOptions={true}
      styles={styles}
      classNamePrefix="countries-select"
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      noOptionsMessage={() => "No results found"}
      components={customComponents}
    />
  );
};

export default CountriesMultiSelect;
